import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import StatChart from '../components/StatChart';
import AdminSupportInbox from '../components/AdminSupportInbox';
import {
  fetchGlobalStats, fetchDailyStats, fetchActiveToday, fetchTraffic,
  FLOW_RATE_USD,
} from '../firebase/adminStatsService';
import './AdminDashboard.css';

// ── ADMIN ANALYTICS ──────────────────────────────────────────────────────────
// Reached only through AdminRoute, and every figure it shows is also gated
// server-side — firestore.rules for the counters, an admin-claim check inside
// the GA4 callable. See functions/adminClaims.js for how the claim is granted.
//
// EVERY NUMBER IS PRE-AGGREGATED. Nothing on this page counts a collection:
// the totals are one document, a timeframe is one small range query, and
// "active today" is a server-side count. See adminStatsService for the costs.

const TIMEFRAMES = ['day', 'week', 'month', 'year'];

const usd = (n) => `$${(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
const int = (n) => (n || 0).toLocaleString();
const pct = (n) => `${((n || 0) * 100).toFixed(1)}%`;
/** `t()` with {placeholders} filled in — the app's convention everywhere. */
const fill = (s, vars) =>
  Object.entries(vars).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(v), s);

function Kpi({ label, value, sub, tone }) {
  return (
    <div className={`adm-kpi${tone ? ` adm-kpi--${tone}` : ''}`}>
      <span className="adm-kpi-label">{label}</span>
      <strong className="adm-kpi-value">{value}</strong>
      {sub && <span className="adm-kpi-sub">{sub}</span>}
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const { t } = useT();
  const [timeframe, setTimeframe] = useState('month');

  const [global, setGlobal] = useState(null);
  const [daily, setDaily] = useState([]);
  const [activeToday, setActiveToday] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trafficLoading, setTrafficLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // In parallel: three independent reads, none of which depends on
      // another's result.
      const [g, d, a] = await Promise.all([
        fetchGlobalStats(),
        fetchDailyStats(timeframe),
        fetchActiveToday().catch(() => null),   // no markers yet on a fresh day
      ]);
      setGlobal(g);
      setDaily(d);
      setActiveToday(a);
    } catch (err) {
      // Almost always a permission error, which here means the claim is set
      // but the token predates it — say the useful thing, not "failed".
      setError(err?.code === 'permission-denied' ? t('adm_err_claim')
        : (err?.message || t('adm_err_load')));
    } finally {
      setLoading(false);
    }
  }, [timeframe, t]);

  useEffect(() => { load(); }, [load]);

  // SEPARATE FROM THE REST. GA4 is a different system with its own failure
  // mode and its own latency; making the database figures wait for it would
  // leave the page blank while an unconfigured property times out.
  useEffect(() => {
    let alive = true;
    setTrafficLoading(true);
    fetchTraffic(timeframe)
      .then(x => { if (alive) setTraffic(x); })
      .catch(() => { if (alive) setTraffic({ configured: false }); })
      .finally(() => { if (alive) setTrafficLoading(false); });
    return () => { alive = false; };
  }, [timeframe]);

  // Summed from the day rows already loaded — no second query for a subtotal.
  const sum = (k) => daily.reduce((a, r) => a + (r[k] || 0), 0);
  const newUsers = sum('newUsers');
  const generations = sum('generations');
  const cost = sum('costUsd');
  const peakDau = daily.reduce((a, r) => Math.max(a, r.activeUsers || 0), 0);

  // ── NO BLENDED PER-IMAGE RATE ─────────────────────────────────────────────
  // The headline spend card used to carry one, computed as total ÷ images. With
  // two flows at 4¢ and 8¢ that averaged to figures like "$0.05/image" — a
  // price NOTHING was ever charged, sitting on the most prominent card on the
  // page. Labelling it "avg" made it accurate and still left the reader doing
  // arithmetic to find out what anything actually costs.
  //
  // The card now shows money only. The real rates are one section below, on the
  // per-flow cards, where each is a single true number rather than a mixture.
  //
  // The rate a flow actually billed at, or NULL when it has produced nothing —
  // in which case the caller substitutes that flow's configured price. It used
  // to fall back to a flat 0.04 here, so the staging card, which runs
  // kontext-max at eight, advertised the OTHER flow's rate.
  const rateOf = (spent, n) => (n > 0 ? spent / n : null);

  // Generations recorded before the per-flow split existed. They are in the
  // total but in neither flow, so the two cards would not add up — said out
  // loud rather than left as an unexplained discrepancy.
  const unattributed = Math.max(
    0,
    (global?.totalGenerations || 0)
      - (global?.renderGenerations || 0) - (global?.stagingGenerations || 0),
  );

  /**
   * A flow card's sub-line: how many images, and what each one cost.
   *
   * The rate is the one ACTUALLY billed — spend divided by count — falling back
   * to the flow's configured price only before anything has been generated, so
   * a card at zero still says what it will cost rather than nothing or, as it
   * did, the other flow's rate.
   */
  const flowSub = (flow, spent, n) => fill(t('adm_flow_sub'), {
    n: int(n),
    rate: usd(rateOf(spent, n) ?? FLOW_RATE_USD[flow]),
  });
  const rangeLabel = t(`adm_tf_${timeframe}`).toLowerCase();
  const dash = '—';

  // "Today", independent of whatever range is selected in the tabs. `daily`
  // always ends at today (see dayRange in adminStatsService), so the last row
  // already carries it — no separate read.
  const postsToday = daily.length ? daily[daily.length - 1].posts : 0;

  return (
    <div className="adm">
      <header className="adm-top">
        <div className="adm-top-titles">
          <h1 className="adm-title">{t('adm_title')}</h1>
          <p className="adm-sub">
            {fill(t('adm_signed_in'), { who: user?.email || user?.uid || '' })}
          </p>
        </div>
        <div className="adm-top-actions">
          <div className="adm-tabs" role="tablist" aria-label={t('adm_title')}>
            {TIMEFRAMES.map(tf => (
              <button
                key={tf}
                role="tab"
                aria-selected={timeframe === tf}
                className={`adm-tab${timeframe === tf ? ' adm-tab--on' : ''}`}
                onClick={() => setTimeframe(tf)}
              >{t(`adm_tf_${tf}`)}</button>
            ))}
          </div>
          <Link className="adm-back" to="/dashboard">← {t('adm_back')}</Link>
        </div>
      </header>

      {error && <div className="adm-error" role="alert">{error}</div>}

      {/* ── PRODUCT ── */}
      <section className="adm-section">
        <h2 className="adm-h2">{t('adm_sec_product')}</h2>
        <div className="adm-kpis">
          <Kpi label={t('adm_total_users')} value={loading ? dash : int(global?.totalUsers)}
            sub={t('adm_all_time')} />
          <Kpi label={t('adm_new_users')} value={loading ? dash : int(newUsers)}
            sub={rangeLabel} />
          <Kpi label={t('adm_active_users')} value={activeToday == null ? dash : int(activeToday)}
            sub={t('adm_today')} />
          <Kpi label={t('adm_generations')} value={loading ? dash : int(generations)}
            sub={fill(t('adm_n_all_time'), { n: int(global?.totalGenerations) })} />
          <Kpi label={t('adm_spend')} value={loading ? dash : usd(cost)} tone="cost"
            sub={fill(t('adm_spend_sub'), { total: usd(global?.totalCostUsd) })} />
        </div>
      </section>

      {/* ── CONTENT ──────────────────────────────────────────────────────────
          What is actually ON the platform, as opposed to the account and
          spend figures above. Two different kinds of number sit here on
          purpose: posts are a lifetime, never-decremented count (see
          totalPosts in adminStatsService) — deleting one doesn't undo the
          fact it was posted — while projects and products are CURRENT counts
          that move both ways, because a project or a listing really can stop
          existing. */}
      <section className="adm-section">
        <h2 className="adm-h2">{t('adm_sec_content')}</h2>
        <div className="adm-kpis">
          <Kpi label={t('adm_total_posts')} value={loading ? dash : int(global?.totalPosts)}
            sub={t('adm_all_time')} />
          <Kpi label={t('adm_posts_today')} value={loading ? dash : int(postsToday)}
            sub={t('adm_today')} />
          <Kpi label={t('adm_projects')} value={loading ? dash : int(global?.projects)}
            sub={t('adm_right_now')} />
          <Kpi label={t('adm_products')} value={loading ? dash : int(global?.products)}
            sub={t('adm_right_now')} />
        </div>

        <div className="adm-charts">
          <div className="adm-card">
            <h3 className="adm-h3">{t('adm_total_posts')}</h3>
            <StatChart
              data={daily.map(d => ({ date: d.date, value: d.posts }))}
              kind="bar" valueLabel={t('adm_total_posts')} emptyLabel={t('adm_no_data')}
            />
          </div>
        </div>
      </section>

      {/* ── AI SPEND BY FLOW ──────────────────────────────────────────────────
          The two paths run different models at different prices, so a single
          total cannot say whether a rising bill is more 3D renders at the cheap
          rate or a handful of stagings at twice that. Each card carries its own
          rate, divided out of what was actually charged rather than printed
          from a constant — so if a model is swapped again, the card reports the
          new rate without anyone remembering to edit this page. */}
      <section className="adm-section">
        <h2 className="adm-h2">{t('adm_sec_ai')}</h2>
        <div className="adm-kpis">
          <Kpi
            label={t('adm_flow_render')}
            value={loading ? dash : usd(sum('renderCostUsd'))}
            tone="cost"
            sub={flowSub('render', sum('renderCostUsd'), sum('renderGenerations'))}
          />
          <Kpi
            label={t('adm_flow_staging')}
            value={loading ? dash : usd(sum('stagingCostUsd'))}
            tone="cost"
            sub={flowSub('staging', sum('stagingCostUsd'), sum('stagingGenerations'))}
          />
          <Kpi
            label={t('adm_flow_render_total')}
            value={loading ? dash : usd(global?.renderCostUsd)}
            sub={fill(t('adm_n_images'), { n: int(global?.renderGenerations) })}
          />
          <Kpi
            label={t('adm_flow_staging_total')}
            value={loading ? dash : usd(global?.stagingCostUsd)}
            sub={fill(t('adm_n_images'), { n: int(global?.stagingGenerations) })}
          />
        </div>

        {/* DIRECTLY UNDER THE CARDS IT EXPLAINS. It was below the charts,
            which put it off-screen exactly when it was needed: both flows read
            $0.00 while the headline says $0.64, and the reason was two charts
            further down. A discrepancy and its explanation have to be visible
            together or the explanation may as well not exist. */}
        {!loading && unattributed > 0 && (
          <p className="adm-muted adm-unattributed">
            {fill(t('adm_flow_unattributed'), { n: int(unattributed) })}
          </p>
        )}

        <div className="adm-charts">
          <div className="adm-card">
            <h3 className="adm-h3">
              {t('adm_flow_render')}
              <span className="adm-note">{usd(sum('renderCostUsd'))} {t('adm_in_range')}</span>
            </h3>
            <StatChart
              data={daily.map(d => ({ date: d.date, value: d.renderGenerations }))}
              kind="bar" valueLabel={t('adm_generations')} emptyLabel={t('adm_no_data')}
            />
          </div>
          <div className="adm-card">
            <h3 className="adm-h3">
              {t('adm_flow_staging')}
              <span className="adm-note">{usd(sum('stagingCostUsd'))} {t('adm_in_range')}</span>
            </h3>
            <StatChart
              data={daily.map(d => ({ date: d.date, value: d.stagingGenerations }))}
              kind="bar" color="var(--admin-warn)" valueLabel={t('adm_generations')}
              emptyLabel={t('adm_no_data')}
            />
          </div>
        </div>

      </section>

      {/* ── ACCOUNTS BY TYPE ──────────────────────────────────────────────────
          The split the trigger already maintains alongside the total, so it
          costs nothing extra to read and can never disagree with it. Each card
          also carries how many arrived in the selected range, which is the
          question a total on its own cannot answer. */}
      <section className="adm-section">
        <h2 className="adm-h2">{t('adm_sec_accounts')}</h2>
        <div className="adm-kpis">
          <Kpi label={t('adm_customers')} value={loading ? dash : int(global?.customers)}
            sub={fill(t('adm_new_in_range'), { n: int(sum('newCustomers')) })} />
          <Kpi label={t('adm_designers')} value={loading ? dash : int(global?.designers)}
            sub={fill(t('adm_new_in_range'), { n: int(sum('newDesigners')) })} />
          <Kpi label={t('adm_shops')} value={loading ? dash : int(global?.shops)}
            sub={fill(t('adm_new_in_range'), { n: int(sum('newShops')) })} />
          {/* Only when there are any. A breakdown that does not add up to the
              total is worth showing; a permanent "0 unclassified" is noise. */}
          {!loading && global?.unclassified > 0 && (
            <Kpi label={t('adm_unclassified')} value={int(global.unclassified)}
              sub={t('adm_no_type')} />
          )}
        </div>

        <div className="adm-charts">
          <div className="adm-card">
            <h3 className="adm-h3">{t('adm_chart_registrations')}</h3>
            <StatChart
              data={daily.map(d => ({ date: d.date, value: d.newUsers }))}
              kind="bar" valueLabel={t('adm_new_users')} emptyLabel={t('adm_no_data')}
            />
          </div>
          <div className="adm-card">
            <h3 className="adm-h3">
              {t('adm_generations')}
              <span className="adm-note">{usd(cost)} {t('adm_in_range')}</span>
            </h3>
            <StatChart
              data={daily.map(d => ({ date: d.date, value: d.generations }))}
              kind="bar" color="var(--admin-warn)" valueLabel={t('adm_generations')}
              emptyLabel={t('adm_no_data')}
            />
          </div>
          <div className="adm-card">
            <h3 className="adm-h3">
              {t('adm_chart_dau')}
              <span className="adm-note">{fill(t('adm_peak'), { n: int(peakDau) })}</span>
            </h3>
            <StatChart
              data={daily.map(d => ({ date: d.date, value: d.activeUsers }))}
              kind="line" color="var(--admin-ok)" valueLabel={t('adm_active_users')}
              emptyLabel={t('adm_no_data')}
            />
          </div>
        </div>
      </section>

      {/* ── TRAFFIC (GA4) ── */}
      <section className="adm-section">
        <h2 className="adm-h2">
          {t('adm_sec_traffic')} <span className="adm-note">Google Analytics 4</span>
        </h2>

        {trafficLoading ? (
          <p className="adm-muted">{t('adm_loading_traffic')}</p>
        ) : !traffic?.configured ? (
          <div className="adm-setup">
            <strong>{t('adm_ga_not_connected')}</strong>
            <p>{traffic?.reason || t('adm_ga_no_property')}</p>

            {/* The property answered and refused: the id and the API are fine,
                so the only remaining step is access — and the identity to
                grant is reported rather than guessed, because a 2nd-gen
                function may run as either default service account. */}
            {traffic?.needsPropertyAccess ? (
              <ol>
                <li>{t('adm_ga_step_access')}</li>
                <li>
                  {traffic.serviceAccount ? (
                    <>{t('adm_ga_step_viewer')} <code>{traffic.serviceAccount}</code></>
                  ) : t('adm_ga_step_viewer_generic')}
                </li>
                <li>{t('adm_ga_step_wait')}</li>
              </ol>
            ) : (
              <ol>
                <li>
                  {t('adm_ga_step_id')}{' '}
                  <code>firebase functions:params:set GA4_PROPERTY_ID 123456789</code>
                </li>
                <li>{t('adm_ga_step_viewer_generic')}</li>
                <li>{t('adm_ga_step_api')}</li>
                <li>
                  {t('adm_ga_step_redeploy')}{' '}
                  <code>firebase deploy --only functions:ga4Traffic</code>
                </li>
              </ol>
            )}
          </div>
        ) : (
          <>
            <div className="adm-kpis">
              <Kpi label={t('adm_pageviews')} value={int(traffic.pageViews)} />
              <Kpi label={t('adm_unique')} value={int(traffic.users)} sub={t('adm_distinct')} />
              <Kpi label={t('adm_sessions')} value={int(traffic.sessions)} sub={t('adm_visits')} />
              <Kpi label={t('adm_bounce')} value={pct(traffic.bounceRate)} />
              <Kpi label={t('adm_conversion')} value={pct(traffic.conversionRate)}
                sub={fill(t('adm_conversions_n'), { n: int(traffic.conversions) })} />
            </div>
            <div className="adm-card">
              <h3 className="adm-h3">
                {t('adm_visitors')}
                {traffic.cached && <span className="adm-note">{t('adm_cached')}</span>}
              </h3>
              <StatChart
                data={(traffic.series || []).map(d => ({ date: d.date, value: d.users }))}
                kind="line" valueLabel={t('adm_visitors')} emptyLabel={t('adm_no_data')}
              />
            </div>
          </>
        )}
      </section>

      {/* Not a number like everything above it, so it sits below the
          analytics rather than among them. See AdminSupportInbox. */}
      <AdminSupportInbox />

      <footer className="adm-foot">{t('adm_foot')}</footer>
    </div>
  );
}
